<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { pick } from 'lodash'
import { HotelsSearchRequest } from '@/modules/hotels/interfaces/hotelsSearchRequest'

interface Props {
  message: string
  form: HotelsSearchRequest
}

const props = defineProps<Props>()
const router = useRouter()
const { query } = useRoute()
const values = ref<string[]>([])

onMounted(() => {
  const payload = { ...props.form, ...query } as Record<string, unknown>
  const formatted = {
    cityName: payload.cityName,
    stay: payload.Start && payload.End && `${payload.Start} – ${payload.End}`,
    quantity: payload.Quantity && `${payload.Quantity} ${Number(payload.Quantity) > 1 ? 'habitaciones' : 'habitación'}`,
    guests: payload.GuestCount && `${payload.GuestCount} ${Number(payload.GuestCount) > 1 ? 'huéspedes' : 'huésped'}`,
    hotelName: payload.hotelName,
  }
  values.value = Object.values(pick(formatted, ['cityName', 'hotelName', 'stay', 'quantity', 'guests']))
    .filter(Boolean) as string[]
})
</script>

<template>
  <section class="flex items-center justify-center px-4 py-12 min-h-[60vh]">
    <div class="w-full max-w-lg bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <!-- Top accent bar -->
      <div class="h-1 w-full bg-[#021F58]"></div>

      <div class="p-8 flex flex-col items-center text-center gap-6">
        <!-- Icon -->
        <div class="flex items-center justify-center w-16 h-16 rounded-full bg-[#021F58]/8 border border-[#021F58]/15">
          <span class="material-symbols-outlined text-3xl text-[#021F58]">hotel_class</span>
        </div>

        <!-- Message -->
        <div class="space-y-1">
          <h3 class="text-xl font-bold text-gray-900">Sin disponibilidad</h3>
          <p class="text-sm text-gray-500 leading-relaxed">{{ message }}</p>
        </div>

        <!-- Search params pills -->
        <div v-if="values.length" class="flex flex-wrap gap-2 justify-center">
          <span
            v-for="(value, idx) in values"
            :key="idx"
            class="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-xs font-medium text-gray-700 capitalize"
          >
            {{ value }}
          </span>
        </div>

        <!-- Divider -->
        <div class="w-full border-t border-gray-100"></div>

        <!-- Slot for extra help content -->
        <slot name="help" />

        <!-- CTAs -->
        <div class="flex flex-col sm:flex-row gap-3 w-full">
          <button
            class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#021F58] text-white text-sm font-semibold hover:bg-[#1C3F7A] transition-colors shadow-sm"
            @click="router.push({ name: 'hotel.availability' })"
          >
            <span class="material-symbols-outlined text-base">search</span>
            Nueva búsqueda
          </button>
          <button
            class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            @click="router.back()"
          >
            <span class="material-symbols-outlined text-base">arrow_back</span>
            Volver
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
